// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Deterministic randomness, in the only two shapes this game needs.
//
// `hash32` is the important one. Demand in Riser is **stateless and positional**: how many people
// unit 14 sends down at tick 2200 on day 3 is a hash of (seed, day, phase, unit, index), never a draw
// from a stream. That is not a style preference, it is the whole seed-sharing promise — if demand
// came off a shared stream, building one extra office would shift every later draw and two players on
// the same seed would face different towers. `tests/balance.test.ts` asserts it directly: two runs
// with completely different action logs produce byte-identical arrival streams.
//
// `makeRng` (from the engine) is used only for things the player never compares across runs: the
// opening layout, and cosmetic jitter. Those are separate named streams so that adding a cosmetic
// draw can never perturb the layout.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';

/**
 * A 32-bit integer hash of up to five integers. FNV-1a over the bytes, then an avalanche, because
 * plain FNV-1a on small consecutive integers leaves visible structure in the low bits — and the low
 * bits are exactly what `% range` reads.
 */
export function hash32(a: number, b = 0, c = 0, d = 0, e = 0): number {
  let h = 0x811c9dc5;
  for (const v of [a, b, c, d, e]) {
    let x = v | 0;
    for (let i = 0; i < 4; i++) {
      h ^= x & 0xff;
      h = Math.imul(h, 0x01000193);
      x >>>= 8;
    }
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

/** `hash32` folded into [0, range). */
export const hashRange = (range: number, a: number, b = 0, c = 0, d = 0, e = 0): number =>
  range <= 0 ? 0 : hash32(a, b, c, d, e) % range;

/** `hash32` folded into [0, 1). */
export const hashUnit = (a: number, b = 0, c = 0, d = 0, e = 0): number => hash32(a, b, c, d, e) / 4294967296;

/** A named stream off one seed, so two uses can never perturb each other. */
export function stream(seed: number, name: string): Rng {
  return makeRng(`${seed}:${name}`);
}

export type { Rng };
